import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Mail, Lock, GraduationCap } from "lucide-react";
import { saveReturnUrl, consumeReturnUrl, defaultDashboardFor } from "@/lib/postLoginRedirect";

export default function Login() {
  const { login, loading } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // Detect session-expired redirect from api interceptor
  const params = new URLSearchParams(loc.search);
  const sessionExpired = params.get("session_expired") === "1";
  const fromParam = params.get("from");

  // Persist return URL into sessionStorage so it survives the Google OAuth
  // round-trip (location.state is wiped when we leave our origin).
  React.useEffect(() => {
    if (sessionExpired) {
      toast.warning("Your session expired — please login again");
    }
    const fromState = loc.state?.from?.pathname;
    const candidate = fromParam || fromState;
    if (candidate) saveReturnUrl(candidate);
  }, [sessionExpired, fromParam, loc.state]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name}`);
      const dest = consumeReturnUrl() || fromParam || loc.state?.from?.pathname || defaultDashboardFor(u.role);
      nav(dest, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 grid md:grid-cols-2 gap-10 items-center">
      <div>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass-strong text-xs">
          <GraduationCap className="w-4 h-4 text-fuchsia-300" /> Welcome back
        </div>
        <h1 className="mt-5 font-display text-4xl sm:text-5xl font-semibold leading-tight">Pick up where you left off.</h1>
        <p className="mt-3 text-[var(--text-dim)] max-w-md">Sign in to access live classes, recordings & your batch dashboard.</p>

        <div className="mt-8 glass rounded-2xl p-5">
          <div className="text-xs uppercase tracking-wider text-fuchsia-300 mb-2">New here?</div>
          <div className="text-sm text-[var(--text-dim)]">
            Naye students <Link to="/register" className="text-fuchsia-300 underline">register</Link> kar sakte hain ya humare available <Link to="/courses" className="text-fuchsia-300 underline">courses</Link> dekh sakte hain.
          </div>
          <div className="text-xs text-[var(--text-mute)] mt-3">
            Already enrolled? Apne registered email aur password se login karein. Password bhul gaye? Admin se contact karein — <a href="mailto:info@codewithkamlesh.in" className="text-fuchsia-300 underline">info@codewithkamlesh.in</a>
          </div>
        </div>
      </div>

      <form onSubmit={submit} className="glass-strong rounded-3xl p-6 sm:p-8" data-testid="login-form">
        <div className="font-display text-2xl font-semibold">Login</div>
        <p className="text-sm text-[var(--text-mute)] mt-1">Use your email and password</p>

        <div className="mt-6 space-y-4">
          <label className="block">
            <div className="text-xs text-[var(--text-dim)] mb-1.5">Email</div>
            <div className="relative">
              <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-mute)]" />
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                required
                className="w-full bg-[var(--bg-elev-1)] border border-white/10 rounded-xl pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:border-fuchsia-400/40"
                data-testid="login-email"
                placeholder="you@example.com"
              />
            </div>
          </label>
          <label className="block">
            <div className="text-xs text-[var(--text-dim)] mb-1.5">Password</div>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-mute)]" />
              <input
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type="password"
                required
                className="w-full bg-[var(--bg-elev-1)] border border-white/10 rounded-xl pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:border-fuchsia-400/40"
                data-testid="login-password"
                placeholder="Your password"
              />
            </div>
          </label>
          <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="login-submit">
            {loading ? "Signing in…" : "Sign in"}
          </button>

          <div className="relative my-2">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10" /></div>
            <div className="relative flex justify-center text-xs"><span className="bg-[var(--bg-elev-2)] px-2 text-[var(--text-mute)]">or</span></div>
          </div>

          <button
            type="button"
            onClick={async () => {
              // Self-hosted Google OAuth — uses OUR Google Cloud project credentials.
              // No third-party auth providers. User stays on codewithkamlesh.in throughout.
              try {
                const { data } = await api.get("/auth/google/config");
                if (!data?.configured) { toast.error("Google sign-in unavailable. Please use email/password."); return; }
                // Build state for CSRF protection
                const state = Math.random().toString(36).slice(2) + Date.now().toString(36);
                sessionStorage.setItem("g_oauth_state", state);
                const redirectUri = window.location.origin + "/auth/callback";
                const params = new URLSearchParams({
                  client_id: data.client_id,
                  redirect_uri: redirectUri,
                  response_type: "code",
                  scope: "openid email profile",
                  state,
                  access_type: "online",
                  prompt: "select_account",
                });
                window.location.href = `${data.auth_url}?${params.toString()}`;
              } catch (e) {
                toast.error("Could not start Google sign-in");
              }
            }}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-white/15 bg-white text-[#222] text-sm font-semibold hover:bg-white/90 transition"
            data-testid="google-login-btn"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden>
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.75h3.57c2.08-1.92 3.28-4.74 3.28-8.07z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.75c-.99.66-2.25 1.06-3.71 1.06-2.85 0-5.27-1.92-6.13-4.51H2.18v2.84A11 11 0 0 0 12 23z"/>
              <path fill="#FBBC05" d="M5.87 14.14a6.6 6.6 0 0 1 0-4.28V7.02H2.18a11 11 0 0 0 0 9.96l3.69-2.84z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.02l3.69 2.84C6.73 7.3 9.15 5.38 12 5.38z"/>
            </svg>
            Continue with Google
          </button>

          <div className="text-center text-sm text-[var(--text-dim)]">
            New here? <Link to="/register" className="text-fuchsia-300 hover:underline">Create account</Link>
          </div>
        </div>
      </form>
    </div>
  );
}
